import React, { ChangeEvent, useMemo, useState } from 'react';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import {
    useNoteDetail,
    useProperty,
} from '../../../../contextsProperty/ContextProperties';
import Title from '../../Title';
import Icon from '../../../../../muiComponents/Icon';
import { NOTE_DETAIL_INITIAL_STATE } from '../../../../../utils/property/propertyUtils';
import Button from '../../../../../muiComponents/Button';
import useTranslate from '../../../../../customHooks/useTranslate';
import { Alert, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Label from '../../Label';
import {
    convertDateToDayjs,
    isDateless,
} from '../../../../../utils/formatDates';
import DateInput from '../../../../../muiComponents/DateInput';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import SwitchInput from '../../../../../muiComponents/switchInput/SwitchInput';
import Tooltip from '../../../../../muiComponents/Tooltip';
import { getRandomNumber } from '../../../../../utils/usefulFunctions';
import DeletePopUp from '../../DeletePopUp';
import { NoteDetailState } from '../../../../../types/properties/propertyTypes';

const NewNoteDetail = () => {
    const translate = useTranslate();
    const { modidyCollectionItem } = useProperty();

    const {
        isOpenPanel,
        handleNoteDetailChange,
        id,
        fromDate,
        toDate,
        fromBookingDate,
        message,
        enabled,
    } = useNoteDetail();

    const [validationErrors, setValidationErrors] = useState<{
        [key: string]: string;
    }>({});
    const [isDeletePopUpVisible, setIsDeletePopUpVisible] = useState(false);

    const cancelPanel = () => {
        handleNoteDetailChange(NOTE_DETAIL_INITIAL_STATE);
    };

    const emptyFromDate = isDateless(fromDate);
    const emptyToDate = isDateless(toDate);
    const dateTypeValue = useMemo(() => {
        if (emptyFromDate && emptyToDate) return 'dateless';
        if (fromBookingDate) return 'bookingDate';
        return 'checkInDate';
    }, [fromDate, toDate, fromBookingDate]);

    const handleChange = (
        _event: React.ChangeEvent<HTMLInputElement>,
        value: string
    ) => {
        if (value === 'dateless') {
            handleNoteDetailChange({
                fromDate: null,
                toDate: null,
                fromBookingDate: false,
            });
        } else if (value === 'checkInDate') {
            handleNoteDetailChange({
                fromBookingDate: false,
                fromDate: new Date(),
            });
        } else if (value === 'bookingDate') {
            handleNoteDetailChange({
                fromBookingDate: true,
                fromDate: new Date(),
            });
        }
    };

    const handleValidate = () => {
        const errors: { [key: string]: string } = {};
        if (message.trim() === '') {
            errors.message = translate('required');
        }
        if (dateTypeValue !== 'dateless') {
            const fromDatejs = convertDateToDayjs(fromDate);
            const toDatejs = convertDateToDayjs(toDate);
            if (
                !emptyFromDate &&
                fromDatejs &&
                !emptyToDate &&
                toDatejs &&
                fromDatejs.isAfter(toDatejs)
            ) {
                errors.fromDate = 'from_validation';
                errors.toDate = 'to_validation';
            }
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = () => {
        const isValid = handleValidate();
        if (isValid) {
            const action = !id ? 'add' : 'update';

            modidyCollectionItem('notes', action, {
                id: id || `-${getRandomNumber()}`,
                fromDate,
                toDate,
                fromBookingDate,
                message,
                enabled,
            });
            cancelPanel();
        }
    };

    return (
        <>
            <LateralPanel
                open={isOpenPanel}
                className='flex-column gap-20 properties-description__panel'
            >
                <header className='flex flex-between flex-align-center padding-inline50'>
                    <Title text='add_note' className='text-secondary-color' />

                    <div className='flex gap-20 flex-align-center'>
                        <Tooltip
                            title={translate(enabled ? 'enabled' : 'disabled')}
                        >
                            <div>
                                <SwitchInput
                                    checked={enabled}
                                    labelText={''}
                                    onChangeMethod={(
                                        _e: ChangeEvent<HTMLInputElement>,
                                        v: boolean
                                    ) => {
                                        handleNoteDetailChange({
                                            enabled: v,
                                        });
                                    }}
                                />
                            </div>
                        </Tooltip>
                        <Icon
                            iconName='Close'
                            className='cursor-pointer'
                            onClick={() => cancelPanel()}
                        />
                    </div>
                </header>
                <main className='flex-column gap-30 height100perc overflow-y-auto padding-inline50'>
                    <Alert severity='info'>
                        <p style={{ fontWeight: 'normal' }}>
                            {translate('special_notes_info_subtitle')}
                        </p>
                    </Alert>
                    <div>
                        <Label text='date_label_note' translated />
                        <RadioGroup
                            row
                            aria-labelledby='dates-radio-group-label'
                            name='dates-radio-group'
                            value={dateTypeValue}
                            onChange={handleChange}
                        >
                            <FormControlLabel
                                value={'dateless'}
                                control={<Radio />}
                                label={translate('dateless')}
                            />
                            <FormControlLabel
                                value='checkInDate'
                                control={<Radio />}
                                label={translate('check_in_out')}
                            />
                            <FormControlLabel
                                value='bookingDate'
                                control={<Radio />}
                                label={translate('booking_date')}
                            />
                        </RadioGroup>
                    </div>
                    {dateTypeValue !== 'dateless' ? (
                        <div className='flex-column gap-15'>
                            <Label text='dates' translated />
                            <div className='flex gap-20'>
                                <DateInput
                                    label={translate('from')}
                                    value={fromDate}
                                    onChange={(date: Date | null) => {
                                        handleNoteDetailChange({
                                            fromDate: date,
                                        });
                                    }}
                                    errorMessage={validationErrors?.fromDate}
                                    maxDate={toDate}
                                />
                                <DateInput
                                    label={translate('to')}
                                    value={toDate}
                                    onChange={(date: Date | null) =>
                                        handleNoteDetailChange({ toDate: date })
                                    }
                                    errorMessage={validationErrors?.toDate}
                                    minDate={fromDate}
                                />
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                    <div className='flex-column gap-10'>
                        <Label text='message' translated />
                        <TextInput
                            multiline
                            rows={10}
                            value={message}
                            errorMessage={validationErrors?.message}
                            handleChange={(
                                e: ChangeEvent<HTMLTextAreaElement>
                            ) =>
                                handleNoteDetailChange({
                                    message: e.target.value,
                                })
                            }
                        />
                    </div>
                </main>
                <footer
                    className={`flex ${
                        !id ? 'flex-end' : 'flex-between'
                    } padding-inline50`}
                >
                    {!id ? (
                        ''
                    ) : (
                        <Button
                            variant='contained'
                            className={'delete-button'}
                            onClick={() => {
                                setIsDeletePopUpVisible(true);
                            }}
                        >
                            {translate('delete')}
                        </Button>
                    )}
                    <div className='flex gap-20'>
                        <Button variant='outlined' onClick={cancelPanel}>
                            {translate('cancel')}
                        </Button>
                        <Button variant='contained' onClick={handleSave}>
                            {translate('save')}
                        </Button>
                    </div>
                </footer>
            </LateralPanel>
            {isDeletePopUpVisible && (
                <DeletePopUp
                    open={!!isDeletePopUpVisible}
                    onSave={() => {
                        modidyCollectionItem('notes', 'delete', {
                            id,
                        } as NoteDetailState);
                        setIsDeletePopUpVisible(false);
                        cancelPanel();
                    }}
                    onCancel={() => {
                        setIsDeletePopUpVisible(false);
                        cancelPanel();
                    }}
                />
            )}
        </>
    );
};

export default NewNoteDetail;
